<template>
  <div class="mb-3">
    <div class="d-flex align-items-center mt-4 mb-3" style="display: flex;">
      <div style="min-width: 180px;">
        <router-link
          class="ln-primary ln-subtitle-text-thin"
          :to="{ name: 'document', params: { id: docId }, query: { version: originalVersion } }"
          id="backLink"
          aria-roledescription="Nuoroda"
        >
          <font-awesome-icon icon="arrow-left" class="ln-primary mr-2" aria-hidden="true" />
          Atgal
        </router-link>
      </div>
      <div style="flex: 1; text-align: center;">
        <span class="ln-subtitle-text ln-darkblue">Dokumentų palyginimas</span>
      </div>
      <div style="min-width: 180px;"></div>
    </div>

    <div v-if="isLoading" class="text-info" role="status">
      palaukite ruošiamas palyginimas...
      <b-spinner variant="info" small class="ml-2"></b-spinner>
    </div>

    <div v-else-if="error" class="text-danger" role="alert">
      {{ error }}
    </div>

    <div v-else class="d-flex compare-container">
      <div class="compare-flex-col">
        <div class="ln-border bg-white p-3">
          <div class="d-flex align-items-center mb-2" style="height:40px;">
            <b-form-select
              class="ln-border ln-darkblue"
              style="width: auto;"
              :options="versionOptions"
              :value="oldVersion"
              @change="onOldVersionChange"
              aria-label="Senesnė versija"
            ></b-form-select>
          </div>
          <div class="compare-column left mt-3" v-html="diffHtmlLeft"></div>
        </div>
      </div>

      <div class="compare-flex-col">
        <div class="ln-border bg-white p-3">
          <div class="d-flex align-items-center mb-2" style="height:40px;">
            <b-form-select
              class="ln-border ln-darkblue"
              style="width: auto;"
              :options="versionOptions"
              :value="newVersion"
              @change="onNewVersionChange"
              aria-label="Naujesnė versija"
            ></b-form-select>
          </div>
          <div class="compare-column right mt-3" v-html="diffHtmlRight"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GetDocumentHtml, GetDocumentMeta } from "../Assets/repository";
import HtmlDiff from "htmldiff-js";
import { scopeDocumentStyles } from "@/_Helpers/scope-document-styles";

export default {
  name: "document-compare-page",
  data() {
    return {
      isLoading: true,
      error: "",
      diffHtmlLeft: "",
      diffHtmlRight: "",
      versions: [],
      oldVersion: 1,
      newVersion: 1,
      newVersionDisplay: { number: '', date: '' },
      originalVersion: null
    };
  },
  computed: {
    docId() {
      return this.$route.params.id;
    },
    versionOptions() {
      return this.versions.map(v => ({
        value: v.number,
        text: `v${v.number} (${v.date})${v.isActual ? ' (Aktuali)' : ''}`
      }));
    }
  },
  watch: {
    newVersion: 'load',
    '$route.params.id': function() {
      this.loadVersions().then(this.load);
    }
  },
  created() {
    this.loadVersions().then(this.load);
  },
  mounted() {
    this.originalVersion = sessionStorage.getItem('originalVersion');
  },
  methods: {
    onNewVersionChange(value) {
      this.newVersion = value;
      this.$router.replace({ query: { ...this.$route.query, right: value } });
    },
    onOldVersionChange(value) {
      this.oldVersion = value;
      this.$router.replace({ query: { ...this.$route.query, left: value } });
      this.load();
    },
    async loadVersions() {
      try {
        const metaRes = await GetDocumentMeta(this.docId, 1);
        this.versions = metaRes.data.versions || [];
        const leftParam = Number(this.$route.query.left);
        const rightParam = Number(this.$route.query.right);
        const versionParam = Number(this.$route.query.version);
        const actual = this.versions.find(v => v.isActual) || this.versions[this.versions.length - 1];

        if (Number.isFinite(rightParam) && this.versions.some(v => v.number === rightParam)) {
          this.newVersion = rightParam;
          const nv = this.versions.find(v => v.number === rightParam);
          this.newVersionDisplay = { number: nv.number, date: nv.date };
        } else {
          this.newVersion = actual.number;
          this.newVersionDisplay = { number: actual.number, date: actual.date };
        }

        if (Number.isFinite(leftParam) && this.versions.some(v => v.number === leftParam)) {
          this.oldVersion = leftParam;
        } else if (Number.isFinite(versionParam) && this.versions.some(v => v.number === versionParam)) {
          this.oldVersion = versionParam;
        } else if (this.originalVersion && this.versions.some(v => v.number === this.originalVersion)) {
          this.oldVersion = this.originalVersion;
        } else {
          const prev = this.versions[this.versions.length - 2];
          this.oldVersion = prev ? prev.number : this.versions[0].number;
        }
      } catch (e) {
        this.error = "Nepavyko gauti versijų";
      }
    },
    async load() {
      this.isLoading = true;
      this.error = "";
      this.diffHtmlLeft = "";
      this.diffHtmlRight = "";
      try {
        if (!this.docId) throw new Error("Nėra dokumento ID");
        const [oldRes, newRes] = await Promise.all([
          GetDocumentHtml(this.docId, this.oldVersion),
          GetDocumentHtml(this.docId, this.newVersion)
        ]);

        const oldHtmlRaw = (oldRes && oldRes.data && oldRes.data.htmlDocument) ? oldRes.data.htmlDocument : "";
        const newHtmlRaw = (newRes && newRes.data && newRes.data.htmlDocument) ? newRes.data.htmlDocument : "";
        if (!oldHtmlRaw || !newHtmlRaw) throw new Error("Dokumento HTML negautas");
        await this.$nextTick();
        const diffHtml = HtmlDiff.execute(oldHtmlRaw, newHtmlRaw);

        const scoped = scopeDocumentStyles(diffHtml, ".compare-column");

        this.diffHtmlLeft = scoped;
        this.diffHtmlRight = scoped;

        const newV = this.versions.find(v => v.number === this.newVersion);
        if (newV) {
          this.newVersionDisplay = { number: newV.number, date: newV.date };
          document.title = `Palyginimas — ${this.docId} v${this.oldVersion} / v${this.newVersion}`;
        }
      } catch (e) {
        this.error = (e && e.message) ? e.message : "Nepavyko sugeneruoti palyginimo";
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
    .compare-column /deep/ ins,
    .compare-column /deep/ del {
        text-decoration: none;
}

.compare-column.left /deep/ ins {
  visibility: hidden;
}
.compare-column.right /deep/ del {
  visibility: hidden;
}

.compare-column.left /deep/ del {
  background-color: rgba(255, 51, 0, 0.12);
  text-decoration: line-through;
}
.compare-column.right /deep/ ins {
  background-color: rgba(29, 134, 60, 0.12);
}
.compare-column {
  min-height: 200px;
  background: #f9fafd;
  border-radius: 4px;
  padding: 8px;
  font-size: 1rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.compare-column table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
}
.compare-column table td,
.compare-column table th {
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  overflow: hidden;
  padding: 4px 8px;
}
.compare-container {
  display: flex;
  width: 100vw;
  margin: 0;
  gap: 0;
  overflow-x: hidden;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}
.compare-flex-col {
  width: 50vw;
  flex: none;
  min-width: 0;
}
body, html {
  padding: 0;
  margin: 0;
}
.d-flex > div {
  min-width: 0;
}
#backLink {
  text-decoration: none!important;
  color: rgb(0, 92, 191)!important;
}
.ln-border {
  border: 1px solid #e0e6ed;
  border-radius: 4px;
}
.ln-darkblue {
  color: #003366;
}
@media (max-width: 900px) {
  .compare-container,
  .d-flex[style] {
    flex-direction: column !important;
    max-width: 100% !important;
  }
  .compare-container > div,
  .d-flex > div {
    width: 100% !important;
    min-width: 0 !important;
  }
}
</style>